<template>
  <v-row justify="center">
    <v-dialog :value="dialog" max-width="1000" persistent @input="closeModal">
      <v-card class="card">
        <v-card-title class="headline accent--text pb-4">
          {{ isEdit ? 'Редактировать' : 'Добавить' }}
        </v-card-title>
        <v-card-text outlined tile class="border-top">
          <v-container class="px-0">
            <v-row>
              <h3 class="mb-2 ml-4 mt-6">Выберите заявление</h3>

              <v-col cols="12" class="py-0">
                <v-card class="card" :elevation="1">
                  <v-card-title>
                    <v-autocomplete
                      label="Протокол" placeholder="Выберите из списка"
                      v-model="filter.protocol"
                      :items="protocolItems" item-value="id" item-text="protocolNumber"
                      outlined rounded dense return-object hide-no-data clearable hide-details
                      @change="getApplications"
                    >
                      <template #prepend-item>
                        <div class="ml-4 mb-2">Номер протокола / Дата протокола</div>
                        <v-divider></v-divider>
                      </template>

                      <template v-slot:item="data">
                        {{ data.item?.protocolNumber }} / {{ data.item?.protocolDate }}
                      </template>
                    </v-autocomplete>
                    <v-autocomplete
                      class="ml-2"
                      label="Решение" placeholder="Выберите из списка"
                      v-model="filter.decision"
                      :items="decisionItems" item-text="name"
                      outlined rounded dense return-object clearable hide-details
                      @change="getApplications"
                    />
                  </v-card-title>
                  <v-card-text>
                    <v-data-table
                      class="table elevation-0"
                      :headers="tableHeaders"
                      :loading="applicationLoading"
                      :items="applicationItems.list"
                      :search="applicationSearch"
                      :items-per-page="-1"
                      :height="250"
                      dense hide-default-footer fixed-header
                      no-results-text="Нет подходящих заявлений"
                    >
                      <template #item="{ item, index }">
                        <tr @click="selectApplication(item)" class="cursor-pointer" :class="{ selectedRow: orderLine.application?.id === item.id }" :key="index">
                          <td class="text-start" v-for="header in tableHeaders">{{ item[header.value] }}</td>
                        </tr>
                      </template>

                      <template slot="no-data">
                        <div>Нет заявлений</div>
                      </template>
                    </v-data-table>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" class="py-0 mt-6">
                <v-text-field
                  v-model="orderLine.studentNumber"
                  label="Номер студента"
                  placeholder="Введите номер студента"
                  outlined dense rounded
                  @input="$v.orderLine.studentNumber.$touch()"
                  @blur="$v.orderLine.studentNumber.$touch()"
                  :error-messages="errorMessages.studentNumber"
                />
              </v-col>

              <v-col cols="12" class="py-0">
                <v-text-field
                  v-model="orderLine.contractNumber"
                  label="Номер контракта"
                  placeholder="Введите номер контракта"
                  outlined dense rounded
                  @input="$v.orderLine.contractNumber.$touch()"
                  @blur="$v.orderLine.contractNumber.$touch()"
                  :error-messages="errorMessages.contractNumber"
                />
              </v-col>

              <v-col cols="12" class="py-0">
                <v-autocomplete
                  label="Группа" placeholder="Выберите из списка"
                  v-model="orderLine.group"
                  :items="groupItems" item-text="name"
                  outlined rounded dense return-object
                  @input="$v.orderLine.group.$touch()"
                  @blur="$v.orderLine.group.$touch()"
                  :error-messages="errorMessages.group"
                />
              </v-col>

              <v-col cols="12" class="py-0">
                <UIDatePicker
                  label="Дата контракта"
                  v-model="orderLine.contractDate"
                  :error-messages="errorMessages.contractDate"
                  @input="$v.orderLine.contractDate.$touch()"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions class="px-6">
          <v-spacer></v-spacer>
          <v-btn text color="lightGrey" @click="closeModal"> {{ isShowMode ? 'Закрыть' : 'Отмена' }} </v-btn>
          <v-btn
            v-if="!isShowMode"
            :loading="saveBtnLoading"
            :disabled="$v.$invalid || isShowMode"
            color="success"
            text
            @click="saveOrderLine"
          >
            {{ isEdit ? 'Сохранить' : 'Создать' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import {ApplicationDtoList, OrderLine} from '@/models'
import {requiredObjectId, setValidateMessages} from '@/scripts/validation'
import {
  groupNumberApi,
  applicationApi,
  protocolApi,
  decisionApi,
  protocolLineApi
} from '@/api'
import _, {cloneDeep} from "lodash";
import UIDatePicker from "@/components/ui/UIDatePicker";

export default {
  components: {UIDatePicker},
  props: {
    isShowMode: Boolean,
    saveBtnLoading: Boolean,
    dialog: Boolean,
    selectedItem: Object
  },

  async created() {
    await this.loadCatalogs()
    await this.getApplications()
  },

  data: () => ({
    filter: {
      decision: null,
      protocol: null
    },

    tableHeaders: [
      { text: 'Номер', value: 'id', sortable: false, align: 'start' },
      { text: 'ФИО', value: 'fullName', sortable: false, align: 'start' },
      { text: 'Статус', value: 'applicationStatusText', sortable: false, align: 'start' },
      { text: 'Тип заявления', value: 'applicationTypeText', sortable: false, align: 'start' },
      { text: 'Специальность', value: 'specialityToText', sortable: false, align: 'start' },
      { text: 'Курс', value: 'courseNumberToText', sortable: false, align: 'start' },
      { text: 'Тип обучения', value: 'paidFormToText', sortable: false, align: 'start' },
      { text: 'Дата отправки', value: 'sendDateTimeText', sortable: false, align: 'start' },
    ],
    applicationSearch: '',
    applicationItems: new ApplicationDtoList(),
    applicationLoading: false,

    orderLine: new OrderLine(),

    groupItems: [],

    protocolItems: [],
    decisionItems: []
  }),

  validations() {
    return {
      orderLine: {
        application: { requiredObjectId: requiredObjectId },
        studentNumber: { required },
        group: { required },
        contractNumber: { required },
        contractDate: { required },
      }
    }
  },

  computed: {
    isEdit() {
      return this.selectedItem?.id
    },

    errorMessages() {
      const allErrors = {}

      Object.keys(this.$v.orderLine.$params).forEach(key => {
        allErrors[key] = setValidateMessages(this.$v.orderLine[key])
      })

      return allErrors
    }
  },

  methods: {
    async getApplications() {
      this.applicationLoading = true
      try {
        const data = await applicationApi.findAllByProtocolLineFilter(this.filter)
        this.applicationItems = new ApplicationDtoList(data)
      } catch (e) {
        this.showErrorMessage(e)
      } finally {
        this.applicationLoading = false
      }
    },

    async loadCatalogs() {
      const [group, protocols, decisions] = await Promise.all([
          groupNumberApi.getCatalog(),
          protocolApi.getCatalog(),
          decisionApi.getCatalog(),
      ])

      this.groupItems = group
      this.protocolItems = protocols
      this.decisionItems = decisions
    },

    saveOrderLine() {
      this.$emit('saveOrderLine', this.orderLine.getForShortAPI())
    },

    // getApplicationItems: _.debounce(async function(value) {
    //   try {
    //     if (value?.length >= 3 && !this.applicationItems.find(item => item.name === value)) {
    //       this.applicationLoading = true
    //       this.applicationItems = await applicationApi.findAllByFullName(value)
    //     }
    //   } catch (e) {
    //     this.showErrorMessage(e)
    //   } finally {
    //     this.applicationLoading = false
    //   }
    // }, 500),

    selectApplication(item) {
      this.orderLine.application = cloneDeep(item)
    },

    resetModal() {
      this.$v.$reset()
      this.orderLine = new OrderLine()
    },

    closeModal() {
      this.orderLine = new OrderLine()
      this.$emit('closeModal')
    }
  },

  watch: {
    selectedItem(val) {
      this.orderLine = val ? OrderLine.buildFromAPI(val) : new OrderLine()
    },

    dialog(val) {
      !val && this.resetModal()
    }
  }
}
</script>

<style scoped>
.border-top {
  border: 0 !important;
  border-top: 1px solid rgba(0, 0, 0, 0.12) !important;
}

.card >>> .v-label.theme--light {
  font-size: 14px;
  color: rgba(55, 71, 79, .8) !important;
}

.card >>> .v-label.v-label--active.theme--light {
  font-size: 16px;
}

.card >>> .v-label.v-label--active.primary--text.theme--light {
  color: #2196f3 !important
}

.table >>> tbody tr.selectedRow {
  background-color: #E8E8E8 !important;
}

.table >>> tbody tr.selectedRow:hover {
  background-color: #E8E8E8 !important;
}

.cursor-pointer{
  cursor: pointer;
}

.card {
  border-radius: 20px !important;
}
</style>